import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Dialog, Slide, Box, Chip, TextField, Typography, InputAdornment
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PickupDropForm from "./PickupDropForm";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const fallback = [
  { studentName: "Andrei Bloom", registrationNo: "REG001", grade: "Nilai 5", parentName: "John Bloom", pickupPerson: "Maria Bloom", contactNumber: "9876543210", requestDate: "01/03/2025", status: "Approved" },
  { studentName: "Sarah Connor", registrationNo: "REG002", grade: "Nilai 3", parentName: "James Connor", pickupPerson: "James Connor", contactNumber: "9876543211", requestDate: "05/03/2025", status: "Pending" },
  { studentName: "Emily Davis", registrationNo: "REG003", grade: "Nilai 7", parentName: "Tom Davis", pickupPerson: "Lisa Davis", contactNumber: "9876543212", requestDate: "10/03/2025", status: "Approved" },
  { studentName: "Michael Lee", registrationNo: "REG004", grade: "Nilai 2", parentName: "Susan Lee", pickupPerson: "Susan Lee", contactNumber: "9876543213", requestDate: "12/03/2025", status: "Rejected" },
];

const columns = [
  { label: "Student Name", field: "studentName" },
  { label: "Stu.ID", field: "registrationNo" },
  { label: "Grade", field: "grade" },
  { label: "Parent / Guardian", field: "parentName" },
  { label: "Pickup Person", field: "pickupPerson" },
  { label: "Contact", field: "contactNumber" },
  { label: "Request Date", field: "requestDate" },
  { label: "Status", field: "status" },
];

function PickupDrop() {
  const [open, setOpen] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadPickupDrop = useCallback(async () => {
    try {
      const response = await fetchData("student/pickup-and-drop/list");
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        const mapped = data.map((r) => ({
          ...r,
          requestDate: r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB") : "—",
          status: r.status || "Pending",
        }));
        setRawData(mapped.length > 0 ? mapped : fallback);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadPickupDrop(); }, [loadPickupDrop]);

  const displayRows = filterRows(rawData, filterText);

  const handleExport = () => exportToCSV(columns, displayRows, "pickup_drop.csv");

  const handleDeleteConfirm = () => {
    setRawData((prev) => filterRows(prev, filterText).length === prev.length
      ? [] : prev.filter((r) => !filterRows([r], filterText).length));
    setRawData([]);
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    button: { background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", fontSize: "14px", textTransform: "none", padding: "8px 20px", borderRadius: "8px" },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
  };

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <Dialog open={open} TransitionComponent={Transition} keepMounted sx={{ "& .MuiPaper-root": { borderRadius: "20px" } }}>
        <PickupDropForm handleClose={() => { setOpen(false); loadPickupDrop(); }} />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>Pages / Pick-up & Drop</p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Pick-up & Drop</h2>
          <Chip label={`${displayRows.length} Requests`} sx={styles.badge} />
        </Box>
        <Button onClick={() => setOpen(true)} style={styles.button}>+ Schedule Pick up/ Drop</Button>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: showFilter ? 1 : 2, flexWrap: "wrap" }}>
        <Button startIcon={<DeleteOutlineIcon />} sx={styles.actionBtn} onClick={() => setDeleteConfirm(true)}>Delete</Button>
        <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
        <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by name, status, grade..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <DynamicTable columns={columns} rows={displayRows} />
    </div>
  );
}

export default PickupDrop;
